interface userOrganizationMembership {
    id: string,
    name: string,
    role: string,
    created_at: string,
    updated_at: string,
}

interface OrganizationMember {
    id: string,
    email: string,
    full_name: string,
    role: string,
}

interface OrganizationWithUsers {
    id: string,
    name: string,
    created_at: string,
    updated_at: string,
    members: OrganizationMember[],
}

interface CreateOrganizationResponse {
    id: string,
    name: string,
    created_at: string,
}

interface getUserResponse {
    id: string,
    email: string,
    full_name: string,
    is_verified: boolean,
    created_at: string,
    updated_at: string,
    organizations: userOrganizationMembership[],
}

interface SlackInstallationStatus {
    is_connected: boolean;
    status?: 'connected' | 'error_externally_disconnected';
    team_name?: string;
    team_id?: string;
    connected_at?: string;
    scopes?: string[];
}

interface GitHubInstallationStatus {
    is_connected: boolean;
    status?: 'connected' | 'error_externally_disconnected';
    account_login?: string;
    installation_id?: string;
    connected_at?: string;
}

interface GitHubRepository {
    id: number;
    name: string;
    full_name: string;
    private: boolean;
    description?: string;
    html_url: string;
}

interface GitHubTeam {
    id: number;
    name: string;
    slug: string;
    description?: string;
    privacy: string;
    html_url: string;
}

interface GitHubLabel {
    id: number;
    name: string;
    color: string;
    description?: string;
    repository_id: number;
    repository_full_name: string;
}

class ApiService {
    private readonly baseURL: string;
    private readonly defaultHeaders: Headers;
    private readonly email?: string;
    private readonly password?: string;

    constructor(baseURL: string, email?: string, password?: string) {
        this.baseURL = baseURL;
        this.email = email;
        this.password = password;
        this.defaultHeaders = new Headers({'Content-Type': 'application/json'})
    }

    // Create an ApiService from an existing session (no login needed)
    static fromSession(baseURL: string): ApiService {
        return new ApiService(baseURL);
    }

    // Check if there's a valid session by trying to fetch user
    static async checkSession(baseURL: string): Promise<boolean> {
        try {
            const resp = await fetch(`${baseURL}/user`, {
                method: 'GET',
                headers: {'Content-Type': 'application/json'},
                credentials: 'include',
            });
            return resp.ok;
        } catch {
            return false;
        }
    }

    async register(email: string, password: string, fullName: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/users`, {
            method: 'POST',
            body: JSON.stringify({ email, password, full_name: fullName }),
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 409) {
                throw new Error("An account with this email already exists");
            } else if (resp.status === 400) {
                const data = await resp.json();
                throw new Error(data.error || "Invalid registration data");
            } else {
                throw new Error("Failed to create account");
            }
        }
    }

    async login(): Promise<void> {
        if (!this.email || !this.password) {
            throw new Error("Cannot login without credentials");
        }
        const resp = await fetch(`${this.baseURL}/login`, {
            method: 'POST',
            body: JSON.stringify({email: this.email, password: this.password}),
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when logging in");
            } else {
                throw new Error("failed to login to API client");
            }
        }
    }

    async getUser(): Promise<getUserResponse> {
        const resp = await fetch(`${this.baseURL}/user`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting user details");
            } else {
                throw new Error("failed to get user details");
            }
        }

        return resp.json();
    }

    async getOrganizations(): Promise<OrganizationWithUsers[]> {
        const resp = await fetch(`${this.baseURL}/organizations`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting organizations");
            } else {
                throw new Error("failed to get organizations");
            }
        }

        return resp.json();
    }

    async createOrganization(name: string): Promise<CreateOrganizationResponse> {
        const resp = await fetch(`${this.baseURL}/organizations`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
            body: JSON.stringify({ name }),
        });

        if (!resp.ok) {
            if (resp.status === 400) {
                const data = await resp.json();
                throw new Error(data.error || "Invalid organization data");
            } else if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when creating organization");
            } else {
                throw new Error("failed to create organization");
            }
        }

        return resp.json();
    }

    async getSlackInstallationStatus(orgId: string): Promise<SlackInstallationStatus> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/slack/installation`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting Slack installation status");
            } else {
                throw new Error("failed to get Slack installation status");
            }
        }

        return resp.json();
    }

    async disconnectSlack(orgId: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/slack/installation`, {
            method: 'DELETE',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else {
                throw new Error("Failed to disconnect Slack");
            }
        }
    }

    async getSlackChannels(orgId: string): Promise<SlackChannel[]> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/slack/channels`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("No Slack installation found for this organization");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting Slack channels");
            } else {
                throw new Error("failed to get Slack channels");
            }
        }

        return resp.json();
    }

    async getGitHubInstallationStatus(orgId: string): Promise<GitHubInstallationStatus> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/github/installation`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting GitHub installation status");
            } else {
                throw new Error("failed to get GitHub installation status");
            }
        }

        return resp.json();
    }

    async disconnectGitHub(orgId: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/github/installation`, {
            method: 'DELETE',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else {
                throw new Error("Failed to disconnect GitHub");
            }
        }
    }

    async getGitHubRepositories(orgId: string): Promise<GitHubRepository[]> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/github/repositories`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("No GitHub installation found for this organization");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting GitHub repositories");
            } else {
                throw new Error("failed to get GitHub repositories");
            }
        }

        return resp.json();
    }

    async getGitHubTeams(orgId: string): Promise<GitHubTeam[]> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/github/teams`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("No GitHub installation found for this organization");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting GitHub teams");
            } else {
                throw new Error("failed to get GitHub teams");
            }
        }

        return resp.json();
    }

    async getGitHubLabels(orgId: string): Promise<GitHubLabel[]> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/github/labels`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("No GitHub installation found for this organization");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting GitHub labels");
            } else {
                throw new Error("failed to get GitHub labels");
            }
        }

        return resp.json();
    }

    // Rule management methods
    async createRule(orgId: string, rule: CreateRuleRequest): Promise<RuleResponse> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/rules`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
            body: JSON.stringify(rule),
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when creating rule");
            } else {
                throw new Error("failed to create rule");
            }
        }

        return resp.json();
    }

    async getRules(orgId: string): Promise<RuleResponse[]> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/rules`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when getting rules");
            } else {
                throw new Error("failed to get rules");
            }
        }

        return resp.json();
    }

    async updateRule(orgId: string, ruleId: string, rule: CreateRuleRequest): Promise<RuleResponse> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/rules/${ruleId}`, {
            method: 'PUT',
            headers: this.defaultHeaders,
            credentials: 'include',
            body: JSON.stringify(rule),
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("Rule not found");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when updating rule");
            } else {
                throw new Error("failed to update rule");
            }
        }

        return resp.json();
    }

    async deleteRule(orgId: string, ruleId: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/rules/${ruleId}`, {
            method: 'DELETE',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("Rule not found");
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when deleting rule");
            } else {
                throw new Error("failed to delete rule");
            }
        }
    }

    async toggleRule(orgId: string, ruleId: string, active: boolean): Promise<RuleResponse> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/rules/${ruleId}/toggle`, {
            method: 'PATCH',
            headers: this.defaultHeaders,
            credentials: 'include',
            body: JSON.stringify({ active }),
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("Rule not found");
            } else if (resp.status === 409) {
                // Free tier limit error - throw the structured error
                const data: FreeTierLimitError = await resp.json();
                throw new FreeTierLimitException(data);
            } else if (resp.status > 399 && resp.status < 500) {
                throw new Error("client error when toggling rule");
            } else {
                throw new Error("failed to toggle rule");
            }
        }

        return resp.json();
    }

    // Plan management methods
    async getOrganizationPlan(orgId: string): Promise<PlanResponse> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/plan`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else {
                throw new Error("Failed to get organization plan");
            }
        }

        return resp.json();
    }

    async updateOrganizationPlan(orgId: string, plan: 'free' | 'pro', downgradeReason?: string): Promise<UpdatePlanResponse> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/plan`, {
            method: 'PUT',
            headers: this.defaultHeaders,
            credentials: 'include',
            body: JSON.stringify({ plan, downgrade_reason: downgradeReason }),
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 400) {
                const data = await resp.json();
                throw new Error(data.error || "Invalid plan");
            } else {
                throw new Error("Failed to update plan");
            }
        }

        return resp.json();
    }

    async getDowngradePreview(orgId: string): Promise<DowngradePreviewResponse> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/plan/downgrade-preview`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else {
                throw new Error("Failed to get downgrade preview");
            }
        }

        return resp.json();
    }

    async getRulesStatus(orgId: string): Promise<RulesEnableStatus> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/rules/status`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else {
                throw new Error("Failed to get rules status");
            }
        }

        return resp.json();
    }

    // ============== Billing Methods ==============

    async getBillingStatus(orgId: string): Promise<SubscriptionStatus> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/billing`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else {
                throw new Error("Failed to get billing status");
            }
        }

        return resp.json();
    }

    // Returns the Paddle.js config the browser needs to open overlay checkout
    // for the Pro plan. The server knows which price to use, so no price id is
    // passed in. Pro is only granted once Paddle confirms payment via webhook.
    async createCheckoutSession(orgId: string): Promise<CheckoutSession> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/billing/checkout`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 503) {
                throw new Error("Billing is not configured. Please try again later.");
            } else {
                throw new Error("Failed to create checkout session");
            }
        }

        return resp.json();
    }

    // Schedule cancellation at the end of the current billing period. The org
    // keeps Pro until then; it is downgraded to Free when Paddle confirms.
    async cancelSubscription(orgId: string, reason?: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/billing/cancel`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
            body: JSON.stringify({ reason }),
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("No active subscription found");
            } else {
                throw new Error("Failed to cancel subscription");
            }
        }
    }

    // Remove a scheduled cancellation so the subscription keeps running.
    async resumeSubscription(orgId: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/billing/resume`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 404) {
                throw new Error("No resumable subscription found");
            } else {
                throw new Error("Failed to resume subscription");
            }
        }
    }

    // Open a Paddle customer portal session for managing payment details.
    async createPortalSession(orgId: string): Promise<PortalSession> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/billing/portal`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else if (resp.status === 503) {
                throw new Error("Billing is not configured.");
            } else {
                throw new Error("Failed to open billing portal");
            }
        }

        return resp.json();
    }

    async getInvoices(orgId: string): Promise<Invoice[]> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/billing/invoices`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized to access this organization");
            } else {
                throw new Error("Failed to get invoices");
            }
        }

        const data = await resp.json();
        return data.invoices;
    }


    async logout(): Promise<void> {
        await fetch(`${this.baseURL}/logout`, {
            method: 'POST',
            credentials: 'include',
        });
    }

    async resendVerificationEmail(): Promise<{ success: boolean; message: string }> {
        const resp = await fetch(`${this.baseURL}/resend-verification-email`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 400) {
                const data = await resp.json();
                throw new Error(data.error || "Email already verified");
            } else {
                throw new Error("Failed to resend verification email");
            }
        }

        return resp.json();
    }

    async changePassword(currentPassword: string, newPassword: string): Promise<{ success: boolean; message: string }> {
        const resp = await fetch(`${this.baseURL}/user/password`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
            body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 400) {
                const data = await resp.json();
                throw new Error(data.error || "Invalid password");
            } else {
                throw new Error("Failed to change password");
            }
        }

        return resp.json();
    }

    // ============== Invite Methods ==============

    async getMyInvites(): Promise<InviteResponse[]> {
        const resp = await fetch(`${this.baseURL}/invites`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else {
                throw new Error("Failed to get invites");
            }
        }

        const data: ListInvitesResponse = await resp.json();
        return data.invites;
    }

    async acceptInvite(inviteId: string): Promise<AcceptInviteResponse> {
        const resp = await fetch(`${this.baseURL}/invites/${inviteId}/accept`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 404) {
                throw new Error("Invite not found");
            } else if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else {
                throw new Error("Failed to accept invite");
            }
        }

        return resp.json();
    }

    async rejectInvite(inviteId: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/invites/${inviteId}/reject`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 404) {
                throw new Error("Invite not found");
            } else if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else {
                throw new Error("Failed to reject invite");
            }
        }
    }

    async createInvite(orgId: string, email: string, role: string): Promise<CreateInviteResponse> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/invites`, {
            method: 'POST',
            headers: this.defaultHeaders,
            credentials: 'include',
            body: JSON.stringify({ email, role }),
        });

        if (!resp.ok) {
            if (resp.status === 409) {
                const data = await resp.json();
                throw new Error(data.error || "User already invited or is a member");
            } else if (resp.status === 400) {
                const data = await resp.json();
                throw new Error(data.error || "Invalid invite data");
            } else if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized");
            } else {
                throw new Error("Failed to create invite");
            }
        }

        return resp.json();
    }

    async cancelInvite(orgId: string, inviteId: string): Promise<void> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/invites/${inviteId}`, {
            method: 'DELETE',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 404) {
                throw new Error("Invite not found");
            } else if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized");
            } else {
                throw new Error("Failed to cancel invite");
            }
        }
    }

    async getOrganizationMembers(orgId: string): Promise<OrganizationMemberWithStatus[]> {
        const resp = await fetch(`${this.baseURL}/organizations/${orgId}/members`, {
            method: 'GET',
            headers: this.defaultHeaders,
            credentials: 'include',
        });

        if (!resp.ok) {
            if (resp.status === 401) {
                throw new Error("Not authenticated");
            } else if (resp.status === 403) {
                throw new Error("Not authorized");
            } else {
                throw new Error("Failed to get members");
            }
        }

        const data: ListMembersResponse = await resp.json();
        return data.members;
    }
}

interface SlackChannel {
    id: string;
    name: string;
    is_private: boolean;
    is_archived: boolean;
    is_member: boolean;
}

// Rule types
interface CreateRuleRequest {
    name: string;
    description?: string;
    repository_id: number;
    repository_full_name: string;
    filter_team_slugs?: string[];
    filter_labels?: string[];
    targets: RuleTargetRequest[];
}

interface RuleTargetRequest {
    provider: string;
    target_id: string;
    target_name: string;
}

interface RuleResponse {
    id: string;
    organization_id: string;
    name: string;
    description?: string;
    is_active: boolean;
    source: RuleSourceResponse;
    targets: RuleTargetResponse[];
    created_at: string;
}

interface RuleSourceResponse {
    provider: string;
    repository: string;
    filters: RuleFiltersResponse;
}

interface RuleFiltersResponse {
    teams: string[];
    labels: string[];
}

interface RuleTargetResponse {
    id: string;
    provider: string;
    target: string;
    target_name: string;
}

// Invite types
interface InviteResponse {
    id: string;
    email: string;
    role: string;
    organization_id: string;
    organization_name: string;
    invited_by_name: string;
    created_at: string;
}

interface ListInvitesResponse {
    invites: InviteResponse[];
}

interface AcceptInviteResponse {
    organization_id: string;
    role: string;
}

interface CreateInviteResponse {
    id: string;
    email: string;
    role: string;
    organization_id: string;
    created_at: string;
}

interface OrganizationMemberWithStatus {
    id: string;
    email: string;
    full_name: string | null;
    role: string;
    status: 'active' | 'pending';
}

interface ListMembersResponse {
    members: OrganizationMemberWithStatus[];
}

// Plan-related types
interface PlanResponse {
    plan: 'free' | 'pro';
}

interface UpdatePlanResponse {
    plan: string;
    from_plan: string;
}

interface DowngradePreviewResponse {
    rules_to_disable: Array<{ id: string; name: string }>;
}

interface RulesEnableStatus {
    plan: string;
    active_rule_count: number;
    limit: number | null;
    can_enable_more: boolean;
}

interface FreeTierLimitError {
    error: 'free_tier_limit';
    message: string;
    active_rule_id: string;
    active_rule_name: string;
}

// Subscription/Billing types
interface SubscriptionStatus {
    has_subscription: boolean;
    status: 'active' | 'canceled' | 'past_due' | 'paused' | 'trialing' | null;
    plan_id: string | null;
    paddle_subscription_id: string | null;
    paddle_customer_id: string | null;
    current_period_start: string | null;
    current_period_end: string | null;
    cancel_at_period_end: boolean;
    created_at: string | null;
    trial_ends_at: string | null;
}

interface CheckoutSession {
    client_token: string;
    price_id: string;
    sandbox: boolean;
    // Server-signed token binding this checkout to the org; echoed back in
    // Paddle custom_data so the webhook can trust which org it belongs to.
    org_token: string;
}

interface PortalSession {
    url: string;
}

interface Invoice {
    id: string;
    invoice_number: string;
    status: 'paid' | 'pending' | 'overdue' | 'refunded';
    amount: number;
    currency: string;
    issued_at: string;
    paid_at: string | null;
    pdf_url: string | null;
    description: string;
}

class FreeTierLimitException extends Error {
    public readonly data: FreeTierLimitError;

    constructor(data: FreeTierLimitError) {
        super(data.message);
        this.name = 'FreeTierLimitException';
        this.data = data;
    }
}

export {ApiService, FreeTierLimitException};
export type {
    OrganizationWithUsers,
    OrganizationMember,
    SlackInstallationStatus,
    SlackChannel,
    GitHubInstallationStatus,
    GitHubRepository,
    GitHubTeam,
    GitHubLabel,
    CreateRuleRequest,
    RuleTargetRequest,
    RuleResponse,
    RuleSourceResponse,
    RuleFiltersResponse,
    RuleTargetResponse,
    InviteResponse,
    AcceptInviteResponse,
    CreateInviteResponse,
    OrganizationMemberWithStatus,
    PlanResponse,
    UpdatePlanResponse,
    DowngradePreviewResponse,
    RulesEnableStatus,
    FreeTierLimitError,
    SubscriptionStatus,
    CheckoutSession,
    PortalSession,
    Invoice,
};
